import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { chatService } from '../services/chatService';
import { PendingMessage } from '../types/chat';
import { useAuthStore } from '../stores/auth';

interface UseFileUploadOptions {
  selectedSubject: string | null;
  selectedThreadId: string | null;
  onMessageSent?: (message: any) => void;
  onPendingMessage?: (message: PendingMessage) => void;
}

export const useFileUpload = ({
  selectedSubject,
  selectedThreadId,
  onMessageSent,
  onPendingMessage
}: UseFileUploadOptions) => {
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  
  const { user } = useAuthStore();

  // Accepted file types
  const ACCEPTED_FILE_TYPES = [
    // Images
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    // Documents
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    // Audio
    'audio/mpeg',
    'audio/wav',
    'audio/ogg',
    'audio/webm',
    // Video
    'video/mp4',
    'video/webm',
    'video/quicktime'
  ].join(',');

  const validateFile = useCallback((file: File): boolean => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ACCEPTED_FILE_TYPES.split(',');
    
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return false;
    }
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('File type not supported');
      return false;
    }
    
    return true;
  }, [ACCEPTED_FILE_TYPES]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!validateFile(file)) {
      event.target.value = '';
      return;
    }

    setFileToUpload(file);
    toast.success(`File selected: ${file.name}`);
  }, [validateFile]);

  const uploadFile = useCallback(async (file: File, message: string = '', contextType?: string) => {
    if (!selectedSubject) {
      toast.error('Select a subject before uploading attachments.');
      return null;
    }

    const currentSubjectForRequest = selectedSubject;
    const currentThreadIdForRequest = selectedThreadId || `temp-thread-${Date.now()}`;
    
    const tempId = `temp-file-${Date.now()}`;
    const tempFileUrl = URL.createObjectURL(file);
    const localTime = new Date().toISOString();

    const ephemeralMessage: PendingMessage = {
      id: tempId,
      content: message?.trim() ? message.trim() : null,
      message_type: 'FILE',
      role: 'user',
      sender: user?.email || '', 
      subjectId: currentSubjectForRequest,
      thread_id: currentThreadIdForRequest,
      created_at: localTime,
      updated_at: localTime,
      status: 'sending',
      sequence_number: 0,
      tempFileUrl,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    };

    // Add to pending messages
    onPendingMessage?.(ephemeralMessage);
    setIsUploading(true);

    try {
      const response = await chatService.uploadFile(
        file,
        currentThreadIdForRequest,
        currentSubjectForRequest,
        message,
        contextType
      );

      console.log('File uploaded successfully:', response);
      onMessageSent?.(response);
      
      // Clear the file after successful upload
      setFileToUpload(null);
      
      return response;
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
      throw error;
    } finally {
      setIsUploading(false);
      URL.revokeObjectURL(tempFileUrl);
    }
  }, [selectedSubject, selectedThreadId, user, onMessageSent, onPendingMessage]);

  const clearFile = useCallback(() => {
    if (fileToUpload) {
      setFileToUpload(null);
      toast.success('File cleared');
    }
  }, [fileToUpload]);

  const handleUploadError = useCallback((error: Error) => {
    console.error('Upload error:', error);
    setIsUploading(false);
    setFileToUpload(null);
    toast.error('Upload failed. Please try again.');
  }, []);

  return {
    // State
    fileToUpload,
    isUploading,
    acceptedFileTypes: ACCEPTED_FILE_TYPES,
    
    // Actions
    handleFileSelect,
    uploadFile,
    clearFile,
    validateFile,
    handleUploadError,
    setFileToUpload
  };
}; 